import { useEffect, useState } from "react";
import { addDays, format, parseISO } from "date-fns";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { fmtDuration, toDay } from "@/lib/time";

const iconBtn = "w-9 h-9 rounded-full border border-[#103080]/15 bg-white flex items-center justify-center text-[#103080] hover:bg-[#103080] hover:text-white transition-colors duration-200";

export default function TeamReport() {
  const [on, setOn] = useState(toDay(new Date()));
  const [r, setR] = useState(null);

  useEffect(() => {
    api.get("/timesheet/report", { params: { on } }).then((x) => setR(x.data)).catch((e) => toast.error(formatApiError(e)));
  }, [on]);

  const download = async () => {
    try {
      const { data } = await api.get("/timesheet/export", { params: { on }, responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `brightpay-hours-${r.end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!r) return null;
  return (
    <section className="rounded-2xl border border-[#103080]/10 bg-white/70 ambient p-6" data-testid="timesheet-team-report">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h2 className="font-serif-brand font-semibold text-2xl text-[#103080]">Team hours · {r.label}</h2>
          <p className="text-sm text-[#0F172A]/60">{format(parseISO(r.start), "d MMM")} to {format(parseISO(r.end), "d MMM yyyy")} · paid {format(parseISO(r.pay_date), "EEE d MMM")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button className={iconBtn} onClick={() => setOn(toDay(addDays(parseISO(r.start), -1)))} aria-label="Previous period" data-testid="report-prev"><ChevronLeft size={16} /></button>
          <button className={iconBtn} onClick={() => setOn(toDay(addDays(parseISO(r.end), 1)))} aria-label="Next period" data-testid="report-next"><ChevronRight size={16} /></button>
          <button onClick={download} data-testid="report-export-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#103080] text-white font-medium px-5 h-10 text-sm hover:bg-[#0090D0] transition-colors duration-200">
            <Download size={15} /> Export for BrightPay
          </button>
        </div>
      </div>
      <ul>
        {r.rows.map((x) => (
          <li key={x.user_id} className="grid grid-cols-[1fr_auto_auto] gap-4 items-center py-3 border-b border-[#103080]/10 last:border-0 text-sm" data-testid={`report-row-${x.user_id}`}>
            <span className="font-semibold text-[#103080]">{x.name}{!x.works_number && <span className="ml-2 text-[11px] font-normal text-amber-700">no works number</span>}</span>
            <span className="text-[#0F172A]/70">{fmtDuration(x.logged_minutes)} <span className="text-[#0F172A]/40">/ {fmtDuration(x.expected_minutes)}</span></span>
            <span className={`text-xs font-semibold w-24 text-right ${x.submitted_at ? "text-[#0090D0]" : "text-[#0F172A]/40"}`}>{x.submitted_at ? "Submitted" : "Not submitted"}</span>
          </li>
        ))}
      </ul>
      <p className="text-xs text-[#0F172A]/45 mt-3">The CSV has Works Number, First Name, Surname, Number of Hours and Description. In BrightPay, import it as hourly payments and use Match Header Row. Add works numbers on each team member (Settings → Team → Edit) so BrightPay can match employees.</p>
    </section>
  );
}
